import React, { useState, useEffect, memo } from 'react'
import GMap from '..'
import { TYPES } from './const'
import { getInfoWindowContent } from './util'

const DisplayMap = memo(({ onMapLoad, markers, polygons, paths, center }) => {
  const [google, setScript] = useState(null)
  const [map, setMap] = useState(null)
  const [figures, setFigures] = useState([])

  useEffect(() => {
    if (!google || !map) return
    onMapLoad(google, { map, markers, paths, polygons })
  }, [map, google])

  useEffect(() => {
    if (!google || !map) return
    renderItems()
  }, [map, google, markers, paths, polygons])

  useEffect(() => {
    if (map) {
      map.setCenter(center)
    }
  }, [center])

  const renderItems = () => {
    eraseOldFigures()

    renderPaths(paths)
    renderMarkers(markers)
    renderPolygons(polygons)
  }

  const renderPaths = paths => paths && paths.forEach(renderPath)
  const renderMarkers = markers => markers && markers.forEach(renderMarker)
  const renderPolygons = polygons => polygons && polygons.forEach(renderPolygon)

  const renderPath = polyline =>
    polyline &&
    saveFigure({
      newFigure: new google.maps.Polyline({
        objectId:
          polyline.customProperties && polyline.customProperties.objectId,
        map: map,
        path: polyline.path,
        geodesic: true,
        strokeOpacity: 1.0,
        strokeWeight: 2,
        ...polyline.options,
      }),
      rawObj: polyline,
      type: TYPES.POLYLINE,
    })

  const renderMarker = marker =>
    marker &&
    saveFigure({
      newFigure: new google.maps.Marker({
        objectId: marker.customProperties && marker.customProperties.objectId,
        position: marker.position,
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
        },
        ...marker.options,
      }),
      rawObj: marker,
      type: TYPES.MARKER,
    })

  const renderPolygon = polygon =>
    polygon &&
    saveFigure({
      newFigure: new google.maps.Polygon({
        objectId: polygon.customProperties && polygon.customProperties.objectId,
        map: map,
        paths: polygon.path,
        clickable: false,
        ...polygon.options,
      }),
      rawObj: polygon,
      type: TYPES.POLYGON,
    })

  const saveFigure = ({ newFigure, rawObj, type }) => {
    const { infoWindowSettings } = rawObj.options

    newFigure.addListener('click', ({ latLng }) => {
      // do not open the same info window multiple times
      if (newFigure.infoWindow && newFigure.infoWindow.getMap()) {
        return
      }
      const infoWindow = new google.maps.InfoWindow({
        ...infoWindowSettings,
        objectId: newFigure.objectId,
        position: latLng,
        content: getInfoWindowContent({
          type,
          clickCoords: latLng.toJSON(),
          path: rawObj.path,
          fallbackContent: infoWindowSettings.content,
        }),
      })

      newFigure.infoWindow = infoWindow

      closeOpenedInfoWindows()
      infoWindow.open(map)
    })

    setFigures(existingFigures => {
      const newFigures = existingFigures.filter(f => {
        if (
          newFigure.objectId &&
          f.objectId &&
          newFigure.objectId === f.objectId &&
          newFigure.type === f.type
        ) {
          // update opened infoWindow
          if (f.infoWindow && f.infoWindow.getMap()) {
            const infoWindow = new google.maps.InfoWindow({
              ...infoWindowSettings,
              objectId: newFigure.objectId,
              position: f.infoWindow.position,
              content: getInfoWindowContent({
                type,
                clickCoords: f.infoWindow.position.toJSON(),
                path: rawObj.path,
                fallbackContent: infoWindowSettings.content,
              }),
            })

            f.infoWindow.close()

            newFigure.infoWindow = infoWindow
            infoWindow.open(map)
          }

          return false
        }

        return true
      })

      return [...newFigures, newFigure]
    })
  }

  const closeOpenedInfoWindows = () => {
    setFigures(existingFigures => {
      existingFigures.forEach(f => {
        // close all opened infoWindows
        if (f.infoWindow && f.infoWindow.getMap()) {
          f.infoWindow.close()
        }
      })

      return existingFigures
    })
  }

  const onInit = (map, google) => {
    setMap(map)
    setScript(google)
  }

  const eraseOldFigures = () => figures.forEach(figure => figure.setMap(null))

  return (
    <GMap
      onInit={onInit}
      mapSettings={{
        zoom: 12,
        center,
      }}
    />
  )
})

export default DisplayMap



// WEBPACK FOOTER //
// ./src/Components/GMap/DisplayMap/index.jsx